import { centsToEuros, parseMoneyToCents } from '@agos/shared'

import type { CartTotals, DiscountMode } from './cart-math'
import { lineDisplayName } from './cart'
import type { OrderCartLine } from './cart'
import { lineNoteOf } from './line-note'

/**
 * Turning a cart into the order the API stores.
 *
 * Three translations happen here and each of them loses information if it is
 * skipped:
 *
 *   - a **gift** has a synthetic negative id and no product row, so it is sent
 *     as `productId: 0` with the rule recorded alongside it;
 *   - a customised pizza's **toppings** are folded into `customName` as
 *     `Margherita [Šunka]`, because the line has nowhere else to carry them —
 *     and the kitchen parses that bracket straight back out;
 *   - a **restaurant** order's customer name is the table, which is how every
 *     other screen finds it again.
 *
 * Ported from legacy `pages/new-order.tsx`. Pure, so the shape is a test rather
 * than a thing discovered in production.
 */

export interface OrderLinePayload {
  productId: number
  /** §9.85 — the size's label, `"50 cm"`. */
  size?: string
  quantity: number
  unitPrice: number
  customName: string
  boxGroupId?: string
  boxGroupName?: string
  boxSlotLabel?: string
  lunchMealType?: string
  giftRuleId?: number
  giftRuleType?: string
  parts?: { productId?: number; name: string; unitPrice: number; quantity: number }[]
  /**
   * Which rule bounds this composed line — `BACKEND.md` §10A.26.
   *
   * `'kombo'` means the dearest part rather than the sum, because a kombo is priced `max` by design.
   * Absent on everything else, which is what keeps the field additive.
   */
  composition?: 'kombo'
  /**
   * Extra toppings on a catalogue pizza — `BACKEND.md` §10A.80.
   *
   * Which ingredient and how many portions, never what it costs: the server prices each from
   * `pizza_ingredients` at this line's size. `unitPrice` above carries the surcharge for the
   * screen's sake and the server has always discarded it, which is why this field exists.
   */
  extras?: { ingredientId: number; quantity: number }[]
  /**
   * The line's own note for the kitchen - §9.690, `BACKEND.md` §10A.348. Trimmed, and absent when
   * there is none, so an order nobody wrote a note on sends exactly the body it sent before.
   */
  note?: string
  /**
   * §9.713, `BACKEND.md` §10A.366 - the line this one was ordered with, as **its index in this payload's `items`**.
   * Only on an add-on whose parent is in the same payload; stored as `addon_of_id`.
   */
  addonOf?: number
  /** §9.713 - the upsell group the add-on was chosen under; the server prices and limits it from the group. */
  upsellGroupId?: number
}

export interface OrderPayload {
  type: string
  paymentMethod: string
  customerName?: string
  customerPhone?: string
  customerEmail?: string
  customerAddress?: string
  deliveryLat?: number
  deliveryLon?: number
  deliveryCost: number
  discount: number
  notes?: string
  isPreorder?: boolean
  scheduledAt?: string
  employeeId?: number
  manualDiscountType?: DiscountMode
  manualDiscountValue?: number
  manualDiscountOperator?: string | null
  couponCode?: string
  items: OrderLinePayload[]
}

export interface OrderDraft {
  cart: OrderCartLine[]
  totals: CartTotals
  orderType: string
  paymentMethod: string
  tableName?: string
  customerName?: string
  customerPhone?: string
  customerEmail?: string
  customerAddress?: string
  coordinates?: { lat: number; lng: number } | null
  notes?: string
  isPreorder?: boolean
  /** Already an ISO instant; the caller owns the Bratislava conversion. */
  scheduledAt?: string | null
  employeeId?: number | null
  /**
   * Whether the channel seats people, and whether it needs an address - §9.393.
   *
   * The screen computes both from the channel table (`requirementsFor`) before it renders the fields
   * that collect them, and then this file decided the same two things from the slug. Optional and
   * falling back to the slug check, so a caller that does not pass them is unchanged.
   */
  seatsGuests?: boolean
  needsAddress?: boolean
  discountMode: DiscountMode
  discountValue: string
  couponCode?: string | null
  operatorName?: string | null
}

/** Empty strings are not values; the API wants the key absent. */
function optional(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

export function orderLinePayload(line: OrderCartLine): OrderLinePayload {
  return {
    // A gift, a custom pizza and a lunch meal all carry a synthetic negative
    // id. None of them has a product row, so none of them can be stored with
    // one.
    productId: line.productId < 0 ? 0 : line.productId,
    quantity: line.quantity,
    unitPrice: centsToEuros(parseMoneyToCents(line.unitPrice)),
    // §9.420 - the shared rule, so the edit path cannot drift from it again.
    customName: lineDisplayName(line),
    // §9.85 — which size, so `ServerPricing` prices it from the product's own `pizza_sizes` rather
    // than from its base price (`BACKEND.md` §10A.33). Absent for a product sold at one price.
    ...(line.size === undefined ? {} : { size: line.size }),
    ...(line.boxGroupId ? { boxGroupId: line.boxGroupId } : {}),
    ...(line.boxGroupName ? { boxGroupName: line.boxGroupName } : {}),
    ...(line.boxSlotLabel ? { boxSlotLabel: line.boxSlotLabel } : {}),
    // §10A.88 — which course of the day's menu, so the kitchen can box it. Stored and published,
    // never priced from; absent on everything that is not a lunch line.
    ...(line.lunchMealType ? { lunchMealType: line.lunchMealType } : {}),
    ...(line.giftRuleId !== undefined ? { giftRuleId: line.giftRuleId } : {}),
    ...(line.giftRuleType !== undefined ? { giftRuleType: line.giftRuleType } : {}),
    // §10A.14. Cents on the cart, euros on the wire — the same conversion `unitPrice` above makes,
    // because the server bounds the line against these and the two figures have to be comparable.
    ...(line.parts && line.parts.length > 0
      ? {
          parts: line.parts.map((part) => ({
            ...(part.productId !== undefined ? { productId: part.productId } : {}),
            name: part.name,
            unitPrice: centsToEuros(part.unitPriceCents),
            quantity: part.quantity,
          })),
        }
      : {}),
    // §10A.26. Which rule bounds it: a kombo by its dearest pizza, everything else by the sum. The
    // server will not infer this, so a line that does not say is bounded exactly as it was before.
    ...(line.composition ? { composition: line.composition } : {}),
    // §10A.80 — which toppings, never what they cost. `unitPrice` above already carries the
    // surcharge and the server has always discarded it: every positive `productId` is re-read from
    // `products`, so §9.41's ham was free from the day it shipped. This is the field that charges
    // for it, and `customName` keeps the text the kitchen reads.
    ...extrasField(line),
    // §9.690, owner #3 - beside the name, never inside it: `customName` is what the receipt prints.
    ...noteField(line),
  }
}

/**
 * `{ extras }` for a line carrying chosen toppings, nothing otherwise - §10A.80, and §9.730 / `BACKEND.md` §10A.378
 * for the edit.
 *
 * **Shared by creation and the order editor's `itemOf`**, because the edit never sent it: every edit that rewrote
 * the lines re-priced a menu pizza bare while its name kept the toppings (FB-1032). One expression, so the two
 * builders cannot drift on it again - §9.420's reason, on the field that charges.
 */
export function extrasField(line: Pick<OrderCartLine, 'extrasChosen'>): {
  extras?: { ingredientId: number; quantity: number }[]
} {
  return line.extrasChosen && line.extrasChosen.length > 0
    ? { extras: line.extrasChosen.map((each) => ({ ...each })) }
    : {}
}

/**
 * §9.713 - every line of a cart as the order wants it, each add-on naming its parent **by index** in the result.
 *
 * The cart links an add-on to its parent by the parent's `lineId` (client-only, because the cart addresses lines by
 * index and an index moves); the server takes the parent's position in the same payload (`BACKEND.md` §10A.366). The
 * edit path (`order-edit.ts`) uses this too, over the lines it actually sends, so the index is always the index in
 * the array that goes out.
 *
 * **An add-on whose parent is not among these lines goes as an ordinary line** - no `addonOf`, no `upsellGroupId`.
 * The cart removes an add-on with its parent, so this is a defence: a link the server cannot resolve refuses the order.
 */
export function orderLinesPayload(lines: readonly OrderCartLine[]): OrderLinePayload[] {
  return withParentLinks(lines, lines.map(orderLinePayload))
}

/**
 * `payloads[i]` built from `lines[i]`, with `addonOf` (the parent's index in `payloads`) and `upsellGroupId` added to
 * each add-on whose parent is in `lines` - §9.713. Shared by the create path above and the edit path's `itemOf`, so
 * the two builders cannot come to disagree about it (§9.420's reason).
 */
export function withParentLinks<P extends object>(
  lines: readonly OrderCartLine[],
  payloads: readonly P[],
): (P & { addonOf?: number; upsellGroupId?: number })[] {
  const indexOf = new Map<string, number>()
  lines.forEach((line, index) => {
    if (line.lineId !== undefined && line.addonOf === undefined) indexOf.set(line.lineId, index)
  })
  return payloads.map((payload, index) => {
    const line = lines[index]
    const parent = line?.addonOf === undefined ? undefined : indexOf.get(line.addonOf)
    if (line === undefined || parent === undefined) return payload
    return {
      ...payload,
      addonOf: parent,
      ...(line.upsellGroupId === undefined ? {} : { upsellGroupId: line.upsellGroupId }),
    }
  })
}

/** `{ note }` when the line says something for the kitchen, nothing otherwise - §9.690. */
function noteField(line: OrderCartLine): { note?: string } {
  const note = lineNoteOf(line)
  return note === undefined ? {} : { note }
}

export function buildOrderPayload(draft: OrderDraft): OrderPayload {
  // §9.393 - the capability the screen already asked for, then the slug. `na_sekeru` uses tables and
  // the builder collects one; naming the order by the literal `restaurant` threw it away, and the
  // table name is what the room screens key on (§9.383).
  const isDelivery = draft.needsAddress ?? draft.orderType === 'delivery'
  const isRestaurant = draft.seatsGuests ?? draft.orderType === 'restaurant'
  const totalDiscountCents = draft.totals.manualDiscountCents + draft.totals.couponDiscountCents

  return {
    type: draft.orderType,
    paymentMethod: draft.paymentMethod,
    // A table order is found again by its customer name on four other screens.
    customerName: isRestaurant
      ? `Stôl ${draft.tableName?.trim() ?? ''}`.trim()
      : optional(draft.customerName),
    customerPhone: optional(draft.customerPhone),
    customerEmail: optional(draft.customerEmail),
    customerAddress: optional(draft.customerAddress),
    ...(isDelivery && draft.coordinates
      ? { deliveryLat: draft.coordinates.lat, deliveryLon: draft.coordinates.lng }
      : {}),
    // Never charged on a collection, whatever zone the address falls in.
    deliveryCost: isDelivery ? centsToEuros(draft.totals.deliveryCents) : 0,
    discount: centsToEuros(totalDiscountCents),
    notes: optional(draft.notes),
    ...(draft.isPreorder ? { isPreorder: true } : {}),
    ...(draft.scheduledAt ? { scheduledAt: draft.scheduledAt } : {}),
    ...(draft.employeeId != null ? { employeeId: draft.employeeId } : {}),
    // The discount is recorded with who gave it. A discount nobody is named for
    // is a discount nobody can be asked about.
    ...(draft.totals.manualDiscountCents > 0
      ? {
          manualDiscountType: draft.discountMode,
          manualDiscountValue: Number(draft.discountValue),
          manualDiscountOperator: draft.operatorName ?? null,
        }
      : {}),
    ...(draft.couponCode && draft.totals.couponDiscountCents > 0
      ? { couponCode: draft.couponCode }
      : {}),
    // §9.713 - the same lines `orderLinePayload` builds, each add-on naming its parent by index.
    items: orderLinesPayload(draft.cart),
  }
}
